import { FC, useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Alert, Autocomplete, Box, FormInputText, InputLabel, SimpleLabel, Text } from '@hopskipdrive/react-components';
import { RiderLocation } from '@rideiq/riders/features/RiderLocations/Location';
import { PageHeading } from '@rideiq/shared/components/PageHeading';

import { ContactGroupForm } from './ContactGroupForm';
import {
  AuthorizedContactGroupContainer,
  ContactGroupViewContainer,
  Container,
  CreateNewGroupButton,
  DeleteIcon,
  EditIcon,
  ProtocolContainer,
  ProtocolEducationContainer,
} from './MustBeMetProtocolForm.styles';
import { AuthorizedContactGroup, Contact, newMustBeMetProtocolFormType } from './types';
import { useMustBeMetProtocol } from './useMustBeMetProtocol';

interface MustBeMetProtocolFormProps {
  contacts: Contact[];
  accountLocationId?: string;
  placeId?: string;
  defaultValue?: RiderLocation | MustBeMetProtocol;
}

export const MustBeMetProtocolForm: FC<MustBeMetProtocolFormProps> = ({
  contacts,
  accountLocationId,
  placeId,
  defaultValue,
}) => {
  const {
    protocolOptions,
    fetchMustBeMetProtocols,
    getProtocolLabelByValue,
    resetProtocolOptions,
    isOnMasterSchoolList,
  } = useMustBeMetProtocol();
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [protocolInstructions, setProtocolInstructions] = useState<string | null>(null);
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<newMustBeMetProtocolFormType>();

  const watchAuthorizedContactGroups = useWatch({
    control,
    name: 'mustBeMetProtocol.authorizedContactGroups',
    defaultValue:
      defaultValue?.authorizedContactGroups?.map((group) => {
        return { ...group, saved: true };
      }) || [],
  });
  const watchLocationProtocol = useWatch({
    control,
    name: 'mustBeMetProtocol.locationProtocol',
  });

  const append = (newGroup) => {
    setValue('mustBeMetProtocol.authorizedContactGroups', [...watchAuthorizedContactGroups, newGroup], {
      shouldDirty: true,
    });
  };

  const remove = (indexToRemove) => {
    setValue(
      'mustBeMetProtocol.authorizedContactGroups',
      watchAuthorizedContactGroups.filter((_, index) => index !== indexToRemove),
      {
        shouldDirty: true,
      }
    );
  };

  const handleAddGroup = () => {
    setEditIndex(watchAuthorizedContactGroups.length);
    append({
      name: '',
      escalationPhoneNumber: '',
      verifyIdBadge: false,
      verifyRiderName: false,
      verifyRiderBirthdate: false,
    });
  };

  const handleCancel = () => {
    if (editIndex === null || editIndex === undefined) {
      return;
    }

    const group = watchAuthorizedContactGroups[editIndex];

    if (!group.saved) {
      remove(editIndex);
    }

    setEditIndex(null);
  };

  const handleDelete = () => {
    if (editIndex === null || editIndex === undefined) {
      return;
    }

    remove(editIndex);
    setEditIndex(null);
  };

  const handleSaveGroup = (index: number, data: AuthorizedContactGroup) => {
    const updateGroup = watchAuthorizedContactGroups.map((group, idx) => {
      return idx === index ? { ...data, saved: true } : group;
    });

    setValue('mustBeMetProtocol.authorizedContactGroups', updateGroup, {
      shouldDirty: true,
    });
    setEditIndex(null);
    setIsEdit(false);
  };

  useEffect(() => {
    if (accountLocationId) {
      fetchMustBeMetProtocols({ accountLocationId });
    } else if (placeId) {
      fetchMustBeMetProtocols({ placeId });
    } else {
      resetProtocolOptions();
    }
  }, [accountLocationId, placeId]);

  useEffect(() => {
    if (!protocolInstructions && watchLocationProtocol && protocolOptions.length > 0) {
      const protocolMatch = protocolOptions.find((protocol) => protocol.value === watchLocationProtocol);

      setProtocolInstructions(protocolMatch?.instructions || null);
    }
  }, [protocolOptions, watchLocationProtocol]);

  return (
    <Container>
      <ProtocolContainer>
        <Box>
          <InputLabel fieldtype="required" htmlFor="location-protocol">
            Drop-off procedure
          </InputLabel>
          <Controller
            name="mustBeMetProtocol.locationProtocol"
            control={control}
            defaultValue={
              typeof defaultValue?.locationProtocol === 'object'
                ? defaultValue.locationProtocol.value
                : defaultValue?.locationProtocol
            }
            render={({ field: { value, onChange } }) => (
              <Autocomplete
                className={!!errors.mustBeMetProtocol?.locationProtocol ? 'hasError' : ''}
                value={value}
                id={'location-protocol'}
                data-testid={'location-protocol'}
                options={protocolOptions}
                autoHighlight
                getOptionLabel={(option) =>
                  typeof option === 'string' ? getProtocolLabelByValue(option) : option.label || ''
                }
                isOptionEqualToValue={(option, value) => protocolOptions.length === 0 || option.value === value.value}
                renderInput={(params) => (
                  <FormInputText
                    {...params}
                    variant="outlined"
                    error={!!errors.mustBeMetProtocol?.locationProtocol}
                    helperText={errors.mustBeMetProtocol?.locationProtocol?.message}
                  />
                )}
                onChange={(_, value) => {
                  if (typeof value === 'string') {
                    return;
                  }

                  setProtocolInstructions(value?.instructions);
                  onChange(value ? value.value : null);
                }}
              />
            )}
          />
        </Box>
        <Box>
          {protocolInstructions && <ProtocolEducationContainer>{protocolInstructions}</ProtocolEducationContainer>}
        </Box>
      </ProtocolContainer>
      <AuthorizedContactGroupContainer>
        <PageHeading variant="h2">{'Authorized individual groups'}</PageHeading>
        <Text variant="bodyM" fontWeight={300}>
          Designate groups who are authorized to meet the rider at this location.
        </Text>
        {watchAuthorizedContactGroups.length === 0 && (
          <Alert
            severity="error"
            message="You need to create at least one group or disable this feature before saving."
          />
        )}
        {watchAuthorizedContactGroups.map((field, index) => (
          <Box key={field.name}>
            {field.saved && editIndex !== index && (
              <ContactGroupViewContainer>
                <Box display="flex" flexDirection="column" gap=".5rem">
                  <Text variant="h3">{field.name}</Text>
                  <Box display="flex" flexDirection="row" gap=".5rem">
                    {field.verifyIdBadge && <SimpleLabel label="Staff ID" />}
                    {field.verifyRiderName && <SimpleLabel label="Know rider’s name" />}
                    {field.verifyRiderBirthdate && <SimpleLabel label="Know rider’s birthday" />}
                  </Box>
                </Box>
                <Box display="flex" gap="1rem">
                  <DeleteIcon onClick={() => remove(index)} />
                  <EditIcon
                    onClick={() => {
                      setIsEdit(true);
                      setEditIndex(index);
                    }}
                  />
                </Box>
              </ContactGroupViewContainer>
            )}
            {editIndex === index && (
              <ContactGroupForm
                index={index}
                contacts={contacts}
                handleSaveGroup={handleSaveGroup}
                handleCancel={handleCancel}
                handleDelete={handleDelete}
                isOnMasterSchoolList={isOnMasterSchoolList}
                isEdit={isEdit}
                defaultValues={
                  isEdit && editIndex < watchAuthorizedContactGroups.length
                    ? watchAuthorizedContactGroups[editIndex]
                    : undefined
                }
              />
            )}
          </Box>
        ))}
        {editIndex === null && (
          <CreateNewGroupButton
            color="tertiary"
            onClick={handleAddGroup}
            variant="text"
            disabled={watchAuthorizedContactGroups.length === 5}
          >
            + Create new group
          </CreateNewGroupButton>
        )}
        {watchAuthorizedContactGroups.length === 5 && editIndex === null && (
          <Alert
            severity="warning"
            variant="outlined"
            message="You have reached the limit of 5 authorized individual groups for this rider. Consider editing or replacing an existing group. "
          />
        )}
      </AuthorizedContactGroupContainer>
    </Container>
  );
};
